import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, Search, ChevronRight, User2, Calendar, FileText, Bell, Wallet, PackageCheck, TrendingUp, Brain, Sparkles, RefreshCw, Lock } from "lucide-react";
import Layout from "@/components/Layout";
import PlanUsageCard from "@/components/PlanUsageCard";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { parsePhoneNumberFromString } from "libphonenumber-js";

const REVENUE_RANGES = [
  { id: "7d",  label: "Ultimi 7 giorni" },
  { id: "30d", label: "Ultimi 30 giorni" },
  { id: "all", label: "Totale" },
];

function formatEuro(v) {
  return new Intl.NumberFormat("it-IT", {
    style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 2,
  }).format(Number(v) || 0);
}

function RevenueCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl border border-[#E5E5E0] bg-white p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#C5A059] mb-2">
        <Icon className="w-4 h-4" />
        <p className="text-[11px] uppercase tracking-[0.18em] font-medium">{label}</p>
      </div>
      <p className="font-serif-display text-3xl text-[#1A1A1A]">{value}</p>
    </div>
  );
}

const COACH_STATE_TONE = {
  ottimo:     { bg: "bg-emerald-50",  border: "border-emerald-200", text: "text-emerald-700",  label: "Ottimo" },
  buono:      { bg: "bg-sky-50",      border: "border-sky-200",     text: "text-sky-700",      label: "Buono" },
  attenzione: { bg: "bg-amber-50",    border: "border-amber-200",   text: "text-amber-700",    label: "Attenzione" },
  critico:    { bg: "bg-rose-50",     border: "border-rose-200",    text: "text-rose-700",     label: "Critico" },
};

function formatLastUpdated(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function Dashboard() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [clientsWithSales, setClientsWithSales] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [onlyFollowup, setOnlyFollowup] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", age: "", notes: "", telefono: "", email: "" });
  const [phoneError, setPhoneError] = useState("");
  const [saving, setSaving] = useState(false);

  const [revenueRange, setRevenueRange] = useState("30d");
  const [revenue, setRevenue] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(false);

  // AI Performance Coach™
  const [coach, setCoach] = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachForcing, setCoachForcing] = useState(false);

  const fetchAll = async () => {
    // Usiamo Promise.allSettled per essere difensivi: se una delle 3 chiamate
    // fallisce (timeout, 5xx, rete), NON azzeriamo gli altri state. Il
    // principio è: mostrare sempre ciò che riusciamo a caricare, mai un
    // crash totale della Dashboard.
    const [cRes, sRes, ssRes] = await Promise.allSettled([
      api.get("/clients"),
      api.get("/me/subscription"),
      api.get("/clients/sales-summary"),
    ]);

    if (cRes.status === "fulfilled") {
      setClients(cRes.value.data);
    } else {
      // NON sovrascrivere `clients` se già popolato da un render precedente.
      // Log silenzioso: un toast rumoroso a ogni refresh sarebbe peggio.
      console.warn("Dashboard: impossibile ricaricare i clienti", cRes.reason?.message);
    }

    if (sRes.status === "fulfilled") {
      const sData = sRes.value.data;
      setSubscription(sData);
      const key = `trico_80warn_${sData.plan}_${sData.period_end || "lifetime"}`;
      const limit = sData.plan === "free" ? sData.limit_total : sData.limit_period;
      const used = sData.plan === "free" ? sData.used_total : sData.used_period;
      if (limit && used / limit >= 0.8 && used < limit && !sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        toast.warning(`Hai usato ${used} su ${limit} analisi. Valuta l'upgrade.`, {
          action: { label: "Vedi piani", onClick: () => { window.location.href = "/piani"; } },
        });
      }
    }

    if (ssRes.status === "fulfilled") {
      setClientsWithSales(new Set(ssRes.value.data.client_ids_with_sales || []));
    }

    // Un toast SOLO se tutte e 3 le chiamate sono fallite (rete totalmente giù).
    if (cRes.status === "rejected" && sRes.status === "rejected" && ssRes.status === "rejected") {
      toast.error("Connessione al server non disponibile. Riprova.");
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    let cancelled = false;
    setRevenueLoading(true);
    api.get(`/me/revenue?range=${revenueRange}`)
      .then(res => { if (!cancelled) setRevenue(res.data); })
      .catch(() => { if (!cancelled) setRevenue(null); })
      .finally(() => { if (!cancelled) setRevenueLoading(false); });
    return () => { cancelled = true; };
  }, [revenueRange]);

  // Auto-load coaching report al mount (cache 24h lato backend → free se valida)
  // PRO-only: per Free/Basic NON facciamo fetch e mostriamo direttamente la card lock.
  const isPro = (user?.plan || "free").toLowerCase() === "pro";

  const fetchCoaching = async (force = false) => {
    if (!isPro) return;  // hard guard: never call backend on non-pro
    if (force) setCoachForcing(true);
    else setCoachLoading(true);
    try {
      const res = await api.post(`/me/coaching${force ? "?force=true" : ""}`);
      setCoach(res.data);
    } catch {
      // silenzioso: lasciamo il widget in stato "non disponibile"
      setCoach(null);
    } finally {
      setCoachLoading(false);
      setCoachForcing(false);
    }
  };

  useEffect(() => { if (isPro) fetchCoaching(false); }, [isPro]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setPhoneError("");
    // Phone validation: optional, but if provided MUST be a valid international number.
    let telE164 = null;
    const rawTel = (form.telefono || "").trim();
    if (rawTel) {
      const parsed = parsePhoneNumberFromString(rawTel, "IT");
      if (!parsed || !parsed.isValid()) {
        setPhoneError("Numero non valido. Usa il formato internazionale (es. +39 333 1234567).");
        return;
      }
      telE164 = parsed.number; // E.164 format with leading "+"
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        age: form.age ? parseInt(form.age, 10) : null,
        notes: form.notes.trim() || null,
        telefono: telE164,
        email: form.email.trim().toLowerCase() || null,
      };
      await api.post("/clients", payload);
      toast.success("Cliente creato");
      setOpen(false);
      setForm({ name: "", age: "", notes: "", telefono: "", email: "" });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const filtered = clients.filter(c => {
    if (onlyFollowup && !c.needs_followup) return false;
    return c.name.toLowerCase().includes(search.toLowerCase());
  });
  const followupCount = clients.filter(c => c.needs_followup).length;

  return (
    <Layout>
      {/* Hero */}
      <div className="mb-10 animate-fade-up">
        <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">Dashboard</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] mb-3">
          Buongiorno{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
        </h1>
        <p className="text-[#737373] max-w-xl">
          Gestisci i tuoi clienti e le analisi tricologiche. Ogni scheda contiene lo storico completo dei protocolli generati.
        </p>
      </div>

      {/* Plan + usage */}
      <PlanUsageCard subscription={subscription} />

      {/* Risultati economici — dati REALI solo da prodotti venduti */}
      <div className="mt-10 mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] font-semibold">
            Risultati economici
          </span>
          {revenueLoading && (
            <span className="text-[10px] text-[#A3A3A3]">aggiornamento…</span>
          )}
        </div>
        <div className="inline-flex rounded-full border border-[#E5E5E0] bg-white p-1" data-testid="revenue-range">
          {REVENUE_RANGES.map(r => (
            <button
              key={r.id}
              type="button"
              onClick={() => setRevenueRange(r.id)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                revenueRange === r.id
                  ? "bg-[#1A1A1A] text-white"
                  : "text-[#737373] hover:text-[#1A1A1A]"
              }`}
              data-testid={`revenue-range-${r.id}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <div
        className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10"
        data-testid="revenue-cards"
      >
        <RevenueCard
          icon={Wallet}
          label="Guadagno generato"
          value={formatEuro(revenue?.total_revenue_eur)}
          testid="revenue-total"
        />
        <RevenueCard
          icon={PackageCheck}
          label="Prodotti venduti"
          value={revenue?.products_sold ?? 0}
          testid="revenue-products"
        />
        <RevenueCard
          icon={TrendingUp}
          label="Media per cliente"
          value={formatEuro(revenue?.avg_per_client_eur)}
          testid="revenue-avg-client"
        />
        <RevenueCard
          icon={Brain}
          label="Guadagno per analisi"
          value={formatEuro(revenue?.avg_per_analysis_eur)}
          testid="revenue-avg-analysis"
        />
      </div>

      {/* AI Performance Coach™ */}
      <div
        className="rounded-2xl border border-[#E5E5E0] bg-white p-6 mb-10"
        data-testid="ai-performance-coach"
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-[#C5A059]" />
              <p className="text-[11px] uppercase tracking-[0.18em] font-medium text-[#C5A059]">
                AI Performance Coach™
              </p>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1A1A1A] text-white tracking-[0.14em]">
                PRO
              </span>
            </div>
            <p className="text-sm text-[#737373]">
              Consigli sul metodo di lavoro basati sui tuoi dati degli ultimi 30 giorni.
            </p>
          </div>
          {isPro && (
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => fetchCoaching(false)}
                disabled={coachLoading || coachForcing}
                className="text-xs px-3 py-1.5 rounded-full border border-[#E5E5E0] text-[#1A1A1A] hover:border-[#C5A059] disabled:opacity-50 transition-colors flex items-center gap-1.5"
                data-testid="coach-refresh-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${coachLoading ? "animate-spin" : ""}`} />
                Aggiorna analisi
              </button>
              <button
                type="button"
                onClick={() => fetchCoaching(true)}
                disabled={coachLoading || coachForcing}
                className="text-xs px-3 py-1.5 rounded-full bg-[#1A1A1A] hover:bg-[#C5A059] text-white disabled:opacity-50 transition-colors"
                data-testid="coach-force-btn"
                title="Bypassa la cache e rigenera ora"
              >
                {coachForcing ? "Rigenero…" : "Rigenera (force)"}
              </button>
            </div>
          )}
        </div>

        {/* Stato LOCK per Free/Basic — teaser di conversione */}
        {!isPro && (
          <div
            className="rounded-xl border border-[#E5E5E0] bg-gradient-to-br from-[#FAFAF7] to-white p-5 sm:p-6"
            data-testid="coach-lock"
          >
            <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 items-stretch">
              {/* Lock icon */}
              <div className="shrink-0 flex sm:flex-col items-center sm:items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-[#1A1A1A] text-[#C5A059] flex items-center justify-center">
                  <Lock className="w-5 h-5" />
                </div>
                <span className="hidden sm:inline-flex text-[10px] tracking-[0.18em] uppercase text-[#737373]">
                  Solo PRO
                </span>
              </div>

              {/* Teaser preview */}
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#C5A059] mb-2 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" />
                  Esempio analisi AI
                </p>
                <p
                  className="text-[15px] leading-relaxed text-[#1A1A1A] italic mb-4 pl-3 border-l-2 border-[#C5A059]"
                  data-testid="coach-lock-preview"
                >
                  "Stai utilizzando il sistema in modo corretto, ma perdi
                  opportunità nel trasformare le analisi in percorsi completi
                  per il cliente."
                </p>

                <ul className="space-y-1.5 mb-5" data-testid="coach-lock-bullets">
                  <li className="flex gap-2 text-sm text-[#1A1A1A]">
                    <span className="text-[#C5A059] shrink-0">✓</span>
                    <span>Individua dove perdi clienti</span>
                  </li>
                  <li className="flex gap-2 text-sm text-[#1A1A1A]">
                    <span className="text-[#C5A059] shrink-0">✓</span>
                    <span>Ti suggerisce come migliorare il tuo metodo</span>
                  </li>
                </ul>

                <div className="flex flex-col items-start gap-2">
                  <Link
                    to="/piani"
                    className="inline-flex items-center px-5 py-2.5 rounded-full bg-[#1A1A1A] hover:bg-[#C5A059] text-white text-sm font-medium transition-colors"
                    data-testid="coach-upgrade-cta"
                  >
                    Sblocca la tua analisi AI
                  </Link>
                  <span className="text-[11px] text-[#737373]" data-testid="coach-lock-urgency">
                    Analisi aggiornata ogni 15 giorni
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stato vuoto / loading iniziale (solo PRO) */}
        {isPro && !coach && (coachLoading || coachForcing) && (
          <p className="text-sm text-[#737373] py-6 text-center" data-testid="coach-loading">
            Sto analizzando il tuo metodo di lavoro…
          </p>
        )}
        {isPro && !coach && !coachLoading && !coachForcing && (
          <p className="text-sm text-[#737373] py-6 text-center" data-testid="coach-empty">
            Nessun report ancora disponibile. Premi "Aggiorna analisi" per generarne uno.
          </p>
        )}

        {isPro && coach && (() => {
          const r = coach.report || {};
          const tone = COACH_STATE_TONE[r.stato_generale] || COACH_STATE_TONE.attenzione;
          return (
            <div className="space-y-5" data-testid="coach-report">
              {/* Header con stato + punteggio */}
              <div className="flex flex-wrap items-center gap-3">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${tone.bg} ${tone.border} ${tone.text}`}
                  data-testid="coach-stato-badge"
                >
                  {tone.label}
                </span>
                <div className="flex items-baseline gap-1">
                  <span className="font-serif-display text-3xl text-[#1A1A1A]" data-testid="coach-punteggio">
                    {r.punteggio ?? 0}
                  </span>
                  <span className="text-xs text-[#A3A3A3]">/ 100</span>
                </div>
                {coach.generated_at && (
                  <span className="text-[11px] text-[#A3A3A3] ml-auto" data-testid="coach-generated-at">
                    Aggiornato il {formatLastUpdated(coach.generated_at)}
                    {coach.from_cache ? " · (cache)" : ""}
                  </span>
                )}
              </div>

              {/* Sintesi */}
              {r.sintesi && (
                <p className="text-[15px] leading-relaxed text-[#1A1A1A]" data-testid="coach-sintesi">
                  {r.sintesi}
                </p>
              )}

              {/* Osservazioni + Consigli */}
              <div className="grid md:grid-cols-2 gap-5">
                <div data-testid="coach-osservazioni">
                  <p className="text-[11px] uppercase tracking-[0.18em] font-medium text-[#737373] mb-3">
                    Osservazioni
                  </p>
                  <ul className="space-y-2">
                    {(r.osservazioni || []).map((o, i) => (
                      <li key={i} className="flex gap-2 text-sm text-[#1A1A1A]">
                        <span className="text-[#C5A059] font-medium shrink-0">{i + 1}.</span>
                        <span>{o}</span>
                      </li>
                    ))}
                    {(!r.osservazioni || r.osservazioni.length === 0) && (
                      <li className="text-sm text-[#A3A3A3]">Nessuna osservazione disponibile.</li>
                    )}
                  </ul>
                </div>
                <div data-testid="coach-consigli">
                  <p className="text-[11px] uppercase tracking-[0.18em] font-medium text-[#737373] mb-3">
                    Consigli pratici
                  </p>
                  <ul className="space-y-2">
                    {(r.consigli || []).map((c, i) => (
                      <li key={i} className="flex gap-2 text-sm text-[#1A1A1A]">
                        <span className="text-[#C5A059] font-medium shrink-0">{i + 1}.</span>
                        <span>{c}</span>
                      </li>
                    ))}
                    {(!r.consigli || r.consigli.length === 0) && (
                      <li className="text-sm text-[#A3A3A3]">Nessun consiglio disponibile.</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca cliente..."
            className="w-full h-12 pl-12 pr-4 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none"
            data-testid="search-clients-input"
          />
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              className="h-12 px-6 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium transition-all duration-300 flex items-center justify-center gap-2"
              data-testid="new-client-button"
            >
              <Plus className="w-4 h-4" />
              Nuovo cliente
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-3xl">Nuovo cliente</DialogTitle>
              <DialogDescription className="text-sm text-[#737373]">
                Inserisci i dati di contatto: il telefono e l'email abilitano la condivisione del report via WhatsApp ed Email.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 pt-2" data-testid="new-client-form">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">Nome completo *</label>
                <input
                  required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full h-12 px-4 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none"
                  data-testid="client-name-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">Età</label>
                <input
                  type="number" min="0" max="120" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })}
                  className="w-full h-12 px-4 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none"
                  data-testid="client-age-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">Telefono</label>
                <input
                  type="tel" value={form.telefono}
                  onChange={(e) => { setForm({ ...form, telefono: e.target.value }); if (phoneError) setPhoneError(""); }}
                  placeholder="+39 333 1234567"
                  className={`w-full h-12 px-4 rounded-xl border bg-white focus:ring-2 outline-none ${
                    phoneError
                      ? "border-[#D9534F] focus:border-[#D9534F] focus:ring-[#D9534F]/20"
                      : "border-[#E5E5E0] focus:border-[#C5A059] focus:ring-[#C5A059]/20"
                  }`}
                  data-testid="client-phone-input"
                />
                {phoneError ? (
                  <p className="text-xs text-[#D9534F] mt-1.5" data-testid="client-phone-error">{phoneError}</p>
                ) : (
                  <p className="text-xs text-[#A3A3A3] mt-1.5">Formato internazionale, abilita la condivisione su WhatsApp.</p>
                )}
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">Email</label>
                <input
                  type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="cliente@email.com"
                  className="w-full h-12 px-4 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none"
                  data-testid="client-email-input"
                />
                <p className="text-xs text-[#A3A3A3] mt-1.5">Abilita l'invio del report tramite Email.</p>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#737373] mb-2 font-medium">Note</label>
                <textarea
                  rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Storico, allergie, preferenze..."
                  className="w-full px-4 py-3 rounded-xl border border-[#E5E5E0] bg-white focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/20 outline-none resize-none"
                  data-testid="client-notes-input"
                />
              </div>
              <DialogFooter>
                <button
                  type="submit" disabled={saving}
                  className="h-11 px-6 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium transition-all disabled:opacity-50"
                  data-testid="client-save-button"
                >
                  {saving ? "Salvataggio..." : "Salva cliente"}
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Follow-up filter chip — visible only when at least one client needs it */}
      {followupCount > 0 && (
        <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="followup-filter-row">
          <button
            type="button"
            onClick={() => setOnlyFollowup(v => !v)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
              onlyFollowup
                ? "bg-[#1A1A1A] border-[#1A1A1A] text-white"
                : "bg-[#FAF6EE] border-[#C5A059]/40 text-[#8A6B2E] hover:bg-[#F3E8CB]"
            }`}
            data-testid="followup-filter-toggle"
          >
            <Bell className="w-3.5 h-3.5" />
            {onlyFollowup ? "Mostra tutti" : `Solo da contattare · ${followupCount}`}
          </button>
          {onlyFollowup && (
            <span className="text-xs text-[#737373]">
              Clienti con ultima analisi oltre 30 giorni fa
            </span>
          )}
        </div>
      )}

      {/* Clients list */}
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-40 rounded-2xl shimmer" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState hasSearch={!!search} onNew={() => setOpen(true)} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="clients-grid">
          {filtered.map((c, idx) => {
            const hasSales = clientsWithSales.has(c.id);
            return (
              <Link
                key={c.id} to={`/client/${c.id}`}
                className={`group rounded-2xl border p-6 transition-all animate-fade-up ${
                  hasSales
                    ? "border-emerald-200 bg-emerald-50/40 hover:border-emerald-400 hover:shadow-[0_4px_24px_rgba(16,185,129,0.12)]"
                    : "border-[#E5E5E0] bg-white hover:border-[#C5A059] hover:shadow-[0_4px_24px_rgba(197,160,89,0.08)]"
                }`}
                style={{ animationDelay: `${idx * 40}ms` }}
                data-testid={`client-card-${c.id}`}
                data-has-sales={hasSales ? "true" : "false"}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    hasSales ? "bg-emerald-100" : "bg-[#EAE5DF]"
                  }`}>
                    <User2 className={`w-5 h-5 ${hasSales ? "text-emerald-600" : "text-[#C5A059]"}`} />
                  </div>
                  <div className="flex items-center gap-2">
                    {c.needs_followup && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FAF6EE] border border-[#C5A059]/50 text-[#8A6B2E] text-[10px] font-medium"
                        data-testid={`client-needs-followup-${c.id}`}
                        title={`Ultima analisi ${c.days_since_last_analysis} giorni fa`}
                      >
                        <Bell className="w-3 h-3" />
                        Da contattare
                      </span>
                    )}
                    <ChevronRight className={`w-5 h-5 transition-all group-hover:translate-x-1 ${
                      hasSales ? "text-emerald-200 group-hover:text-emerald-500" : "text-[#E5E5E0] group-hover:text-[#C5A059]"
                    }`} />
                  </div>
                </div>
                <h3 className="font-serif-display text-2xl text-[#1A1A1A] mb-1">{c.name}</h3>
                <p className="text-sm text-[#737373] mb-5">
                  {c.age ? `${c.age} anni` : "Età non specificata"}
                </p>
                <div className={`flex items-center gap-4 pt-4 border-t ${hasSales ? "border-emerald-100" : "border-[#E5E5E0]"}`}>
                  <div className="flex items-center gap-1.5 text-xs text-[#737373]">
                    <FileText className="w-3.5 h-3.5" />
                    <span>{c.analyses_count} analisi</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-[#737373]">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{new Date(c.created_at).toLocaleDateString("it-IT")}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </Layout>
  );
}

function EmptyState({ hasSearch, onNew }) {
  if (hasSearch) {
    return (
      <div className="text-center py-20 animate-fade-in">
        <p className="font-serif-display text-2xl text-[#1A1A1A] mb-2">Nessun risultato</p>
        <p className="text-[#737373]">Prova a cercare con un altro nome.</p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-dashed border-[#E5E5E0] p-16 text-center animate-fade-in" data-testid="empty-state">
      <div className="w-16 h-16 mx-auto rounded-2xl bg-[#EAE5DF] flex items-center justify-center mb-6">
        <User2 className="w-7 h-7 text-[#C5A059]" />
      </div>
      <h3 className="font-serif-display text-3xl text-[#1A1A1A] mb-2">Il tuo studio ti aspetta</h3>
      <p className="text-[#737373] mb-8 max-w-md mx-auto">
        Inizia creando la scheda del tuo primo cliente. Potrai poi generare analisi tricologiche con AI e protocolli personalizzati.
      </p>
      <button
        onClick={onNew}
        className="h-12 px-6 rounded-xl bg-[#1A1A1A] hover:bg-[#C5A059] text-white font-medium transition-all inline-flex items-center gap-2"
        data-testid="empty-new-client-button"
      >
        <Plus className="w-4 h-4" />
        Crea primo cliente
      </button>
    </div>
  );
}
